import React, { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import MobilePageHeader from "./mobile/components/MobilePageHeader";
import "./mobile/mobile.css";

/* ============================================================
   ResetPasswordPage
   ----------------------------------------------------------------
   Landing page for the Supabase password-recovery email link.

   Flow:
     1. User taps "Forgot password?" on MobileAuthPage / AuthPage.
     2. Supabase emails them a verification URL whose redirectTo is
        `${origin}/reset-password` (set by both Forgot callers).
     3. The verification URL exchanges the token and redirects here
        with `#access_token=...&type=recovery&...` in the hash.
     4. supabase-js (detectSessionInUrl: true by default) parses the
        hash and emits PASSWORD_RECOVERY on its onAuthStateChange.
     5. This page subscribes for any session-bearing event AND polls
        getSession() once, so it shows the "set new password" form
        as soon as the recovery context is ready.
     6. Submitting calls supabase.auth.updateUser({ password }) and
        redirects to "/" — the recovery session is now upgraded to
        a normal session, so the user lands signed in.

   Hash-encoded Supabase verify errors (e.g. "otp_expired") are
   detected and surfaced as a friendly "link expired" message rather
   than spinning forever.

   Public route: wired in index.tsx and App.tsx alongside the
   existing /review path. The auth gate (MobileApp.tsx, AuthedApp)
   never sees this URL, so a logged-out user clicking a reset link
   isn't bounced to the login screen.
   ============================================================ */

type Status =
  | { kind: "verifying" }
  | { kind: "idle" }
  | { kind: "busy" }
  | { kind: "ok" }
  | { kind: "error"; message: string }
  | { kind: "expired" };

const READY_TIMEOUT_MS = 6000;
const SUCCESS_REDIRECT_MS = 1400;

export default function ResetPasswordPage() {
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState<Status>({ kind: "verifying" });

  useEffect(() => {
    let cancelled = false;

    // Detect Supabase-encoded verify error in the URL hash. Common
    // case: "otp_expired" when the user clicks an old reset link.
    const hash = window.location.hash || "";
    if (/error_description=|error=access_denied/i.test(hash)) {
      setStatus({ kind: "expired" });
      return;
    }

    const markReady = () => {
      if (cancelled) return;
      setStatus((prev) =>
        prev.kind === "verifying" ? { kind: "idle" } : prev
      );
    };

    // Subscribe first so we don't miss PASSWORD_RECOVERY if supabase-js
    // resolves the hash between our subscribe and our getSession poll.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      if (sess) markReady();
    });

    // Belt-and-suspenders: if a session already exists (e.g. the user
    // was already logged in and just navigated here), show the form
    // without waiting for an event.
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (data?.session) markReady();
      } catch {
        /* ignore — fall through to the timeout */
      }
    })();

    // Safety: if nothing resolves in 6s, the link is bad.
    const t = window.setTimeout(() => {
      if (cancelled) return;
      setStatus((prev) =>
        prev.kind === "verifying" ? { kind: "expired" } : prev
      );
    }, READY_TIMEOUT_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
      sub.subscription.unsubscribe();
    };
  }, []);

  const submit = async () => {
    if (!pw1 || pw1.length < 8) {
      setStatus({
        kind: "error",
        message: "Password must be at least 8 characters.",
      });
      return;
    }
    if (pw1 !== pw2) {
      setStatus({ kind: "error", message: "Passwords don't match." });
      return;
    }
    setStatus({ kind: "busy" });
    try {
      const { error } = await supabase.auth.updateUser({ password: pw1 });
      if (error) throw error;
      setStatus({ kind: "ok" });
      window.setTimeout(() => {
        window.location.replace("/");
      }, SUCCESS_REDIRECT_MS);
    } catch (err: any) {
      const raw = String(err?.message || err || "");
      const message =
        /auth session missing|jwt|expired|invalid token|not authenticated/i.test(
          raw
        )
          ? "This reset link has expired. Please request a new one from the login screen."
          : /weak password|password.*short|password.*at least|password.*characters/i.test(
              raw
            )
          ? "Please choose a stronger password (at least 8 characters)."
          : raw || "Couldn't update your password. Please try again.";
      setStatus({ kind: "error", message });
    }
  };

  const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") submit();
  };

  const goLogin = () => {
    window.location.replace("/");
  };

  const showForm =
    status.kind === "idle" ||
    status.kind === "busy" ||
    status.kind === "error";

  return (
    <div className="m-auth" data-mode="reset">
      <MobilePageHeader
        title="Reset password"
        back={{ label: "Log in", onClick: goLogin }}
        showThemeToggle={false}
      />
      <div className="m-auth-scroll">
        <div className="m-auth-brand">
          <img
            src={`${process.env.PUBLIC_URL}/icon-192.png`}
            alt=""
            className="m-auth-logo"
          />
          <div className="m-auth-brand-name">The Estimator</div>
        </div>

        <section className="m-card m-auth-card" aria-label="Set new password">
          <h2 className="m-auth-title">Set a new password</h2>

          {status.kind === "verifying" && (
            <div className="m-auth-msg m-auth-msg--info" role="status">
              Verifying reset link…
            </div>
          )}

          {status.kind === "expired" && (
            <div className="m-auth-msg m-auth-msg--err" role="alert">
              This reset link has expired or is invalid. Please request a
              new one from the login screen.
            </div>
          )}

          {showForm && (
            <>
              <div className="m-field">
                <label className="m-section-label" htmlFor="rp-pw1">
                  New password
                </label>
                <div className="m-auth-pw-row">
                  <input
                    id="rp-pw1"
                    type={show ? "text" : "password"}
                    className="m-input"
                    autoComplete="new-password"
                    value={pw1}
                    onChange={(e) => setPw1(e.target.value)}
                    disabled={status.kind === "busy"}
                    onKeyDown={onEnter}
                    placeholder="At least 8 characters"
                  />
                  <button
                    type="button"
                    className="m-auth-pw-toggle"
                    onClick={() => setShow((v) => !v)}
                    disabled={status.kind === "busy"}
                    aria-label={show ? "Hide password" : "Show password"}
                  >
                    {show ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              <div className="m-field">
                <label className="m-section-label" htmlFor="rp-pw2">
                  Confirm new password
                </label>
                <input
                  id="rp-pw2"
                  type={show ? "text" : "password"}
                  className="m-input"
                  autoComplete="new-password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  disabled={status.kind === "busy"}
                  onKeyDown={onEnter}
                />
              </div>

              {status.kind === "error" && (
                <div className="m-auth-msg m-auth-msg--err" role="alert">
                  {status.message}
                </div>
              )}

              <button
                type="button"
                className="m-auth-primary"
                onClick={submit}
                disabled={status.kind === "busy"}
              >
                {status.kind === "busy" ? "Updating…" : "Update password"}
              </button>
            </>
          )}

          {status.kind === "ok" && (
            <div className="m-auth-msg m-auth-msg--info" role="status">
              Password updated. Taking you to the app…
            </div>
          )}

          <div className="m-auth-links">
            <button
              type="button"
              className="m-auth-link"
              onClick={goLogin}
              disabled={status.kind === "busy"}
            >
              ← Back to log in
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
